import React, {CSSProperties, ReactNode, useEffect, useMemo, useRef} from 'react'

interface Props<ItemType> {
  /** The data used to render each item. */
  items: ItemType[]

  /** The function used to render each item. */
  renderer: (item: ItemType, index?: number) => ReactNode

  /** Space between items. */
  rowGap?: number

  /** Space between items. */
  columnGap?: number

  /** Callback for when we reach the end of the loaded items. */
  onReachEnd?: () => void

  /** when true, and root prop is specified, onReachEnd will not be called unless the listEndRef div's offsetTop is actually below the height of the root (i.e. the user actually scrolled to the end) */
  requireUserScrollToEnd?: boolean

  /** Number of columns in the grid (omit for one). */
  numColumns?: number

  /** custom root for IntersectionObserver */
  root?: Element | null

  /** custom rootMargin for IntersectionObserver */
  rootMargin?: string

  /** a flag, when set, will render items in reverse order*/
  scrollReverse?: boolean
}

const listEndPlaceholderStyle = {
  width: '1px',
  height: '1px'
}

/**
 * Renders a vertical list of items
 */
export function List<ItemType>({
  items,
  renderer,
  onReachEnd,
  rowGap = 0,
  columnGap = 0,
  numColumns = 1,
  rootMargin = '500px',
  root = null,
  scrollReverse = false,
  requireUserScrollToEnd
}: Props<ItemType>) {
  const listStyle = useMemo<CSSProperties>(() => {
    let columnText = '1fr'
    for (let i = 1; i < numColumns; i++) {
      columnText += ' 1fr'
    }
    return {
      display: numColumns > 1 ? 'grid' : 'flex',
      flexDirection: scrollReverse ? 'column-reverse' : 'column',
      gridTemplateColumns: columnText,
      rowGap: `${rowGap}px`,
      columnGap: `${columnGap}px`
    }
  }, [rowGap, columnGap, numColumns, scrollReverse])

  const listEndRef = useRef<HTMLDivElement | null>(null)

  useEffect(() => {
    const endRefValue = listEndRef.current

    const endDidShow = (entries: IntersectionObserverEntry[]) => {
      if (!onReachEnd) {
        return
      }

      const [entry] = entries
      if (entry && entry.isIntersecting) {
        const scrollRequirement = scrollReverse
          ? (entry.target as HTMLDivElement).offsetTop < 0
          : !root || (entry.target as HTMLDivElement).offsetTop > root.clientHeight

        if (!requireUserScrollToEnd || scrollRequirement) {
          onReachEnd()
        }
      }
    }

    const observer = new IntersectionObserver(endDidShow, {
      root,
      rootMargin,
      threshold: 1
    })

    if (endRefValue) {
      observer.observe(endRefValue)
    }

    return () => {
      if (endRefValue) {
        observer.unobserve(endRefValue)
      }
    }
  }, [onReachEnd, requireUserScrollToEnd, root, rootMargin, scrollReverse])

  return (
    <div style={listStyle}>
      {items.map(renderer)}
      <div key="list-end-ref" style={listEndPlaceholderStyle} ref={listEndRef}></div>
    </div>
  )
}
